import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, Building2, CreditCard, Clock, ShieldCheck, AlertTriangle, Scale, Handshake, Gavel, BookOpen } from 'lucide-react';
import Footer from '../../components/footer';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

const Section = ({ icon: Icon, number, title, children, isDarkMode }) => (
  <div className="mb-10">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${isDarkMode ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'}`}>
        {number}
      </div>
      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    </div>
    <div className={`leading-relaxed space-y-3 pl-[52px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
      {children}
    </div>
  </div>
);

const KetentuanLayanan = () => {
  const { isDarkMode } = useDarkMode();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-28 pb-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Ketentuan Layanan</h1>
          <p className="text-gray-300 text-lg">Syarat dan Ketentuan Penggunaan Layanan Telnet Indonesia</p>
          <p className="text-gray-400 text-sm mt-2">Terakhir diperbarui: 1 Februari 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 py-12">
        <div className={`rounded-2xl shadow-sm border p-6 sm:p-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>

          <div className={`mb-10 p-6 rounded-xl border ${isDarkMode ? 'bg-green-900/20 border-green-800/40' : 'bg-green-50 border-green-100'}`}>
            <p className={`leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Dengan menggunakan layanan PT Telekomunikasi Internet Indonesia ("Telnet Indonesia"), Anda menyetujui seluruh syarat dan ketentuan yang tertuang dalam dokumen ini. Harap membaca dengan seksama sebelum mendaftar atau menggunakan layanan kami.
            </p>
          </div>

          <Section number="1" title="Definisi Istilah" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>"Penyedia Layanan"</strong> adalah PT Telekomunikasi Internet Indonesia (Telnet Indonesia), berkedudukan di Banda Aceh, Aceh.</li>
              <li><strong>"Pelanggan"</strong> adalah individu atau badan hukum yang terdaftar dan menggunakan layanan Telnet Indonesia.</li>
              <li><strong>"Layanan"</strong> mencakup seluruh produk dan jasa yang disediakan, termasuk namun tidak terbatas pada: Internet Broadband, Internet Dedicated, Metro-E, IPTV, CCTV, VPN, IT Support, dan solusi IT lainnya.</li>
              <li><strong>"Perangkat"</strong> adalah seluruh peralatan yang dipasang oleh Telnet Indonesia di lokasi pelanggan, termasuk router, ONT, kabel fiber optic, dan perangkat pendukung lainnya.</li>
              <li><strong>"Kontrak"</strong> adalah perjanjian berlangganan antara Pelanggan dan Penyedia Layanan.</li>
              <li><strong>"SLA"</strong> (Service Level Agreement) adalah jaminan tingkat layanan yang disepakati.</li>
              <li><strong>"Masa Berlangganan"</strong> adalah periode waktu sejak aktivasi layanan hingga berakhirnya kontrak.</li>
            </ul>
          </Section>

          <Section number="2" title="Syarat Berlangganan" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li>Pelanggan harus berusia minimal <strong>17 tahun</strong> atau memiliki persetujuan dari orang tua/wali yang sah.</li>
              <li>Pelanggan wajib menyediakan <strong>identitas yang sah</strong> (KTP/Paspor untuk perorangan, atau Akta Pendirian/SIUP untuk badan hukum).</li>
              <li>Lokasi instalasi harus berada dalam <strong>area coverage</strong> Telnet Indonesia yang dapat dicek melalui website atau customer service.</li>
              <li>Pelanggan wajib menyediakan <strong>akses yang memadai</strong> ke lokasi instalasi untuk tim teknisi kami.</li>
              <li>Pelanggan menyetujui untuk memberikan informasi yang <strong>akurat dan terkini</strong> selama proses pendaftaran.</li>
              <li>Pendaftaran dianggap sah setelah <strong>verifikasi data</strong> dan <strong>pembayaran biaya aktivasi</strong> (jika ada) diterima.</li>
            </ul>
          </Section>

          <Section number="3" title="Hak dan Kewajiban Pelanggan" isDarkMode={isDarkMode}>
            <p><strong>Hak Pelanggan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Mendapatkan layanan sesuai paket yang dipilih dan dibayarkan</li>
              <li>Mendapatkan dukungan teknis 24/7 melalui customer service</li>
              <li>Mendapatkan informasi terkait gangguan, pemeliharaan, atau perubahan layanan</li>
              <li>Mengajukan keluhan dan mendapatkan penyelesaian dalam waktu yang wajar</li>
              <li>Mengubah paket layanan (upgrade/downgrade) sesuai ketersediaan</li>
              <li>Memperoleh kompensasi sesuai SLA jika terjadi gangguan yang melebihi batas yang ditentukan</li>
            </ul>
            <p className="mt-4"><strong>Kewajiban Pelanggan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Membayar tagihan tepat waktu sesuai tanggal jatuh tempo</li>
              <li>Menjaga perangkat yang dipinjamkan oleh Telnet Indonesia dengan baik</li>
              <li>Tidak menggunakan layanan untuk aktivitas ilegal, termasuk namun tidak terbatas pada: hacking, distribusi malware, konten terlarang, atau pelanggaran hak cipta</li>
              <li>Tidak menjual kembali (resale) layanan tanpa persetujuan tertulis dari Telnet Indonesia</li>
              <li>Melaporkan kerusakan atau gangguan perangkat dalam waktu <strong>1x24 jam</strong></li>
              <li>Memberikan akses ke lokasi instalasi untuk keperluan maintenance terjadwal</li>
            </ul>
          </Section>

          <Section number="4" title="Hak dan Kewajiban Penyedia Layanan" isDarkMode={isDarkMode}>
            <p><strong>Hak Penyedia Layanan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Melakukan verifikasi data dan identitas calon pelanggan</li>
              <li>Menolak pendaftaran yang tidak memenuhi syarat atau berada di luar area coverage</li>
              <li>Melakukan maintenance terjadwal dengan pemberitahuan minimal <strong>24 jam</strong> sebelumnya</li>
              <li>Menangguhkan layanan sementara jika pelanggan melanggar ketentuan penggunaan</li>
              <li>Memutus layanan jika pelanggan tidak membayar tagihan selama lebih dari <strong>30 hari</strong> dari tanggal jatuh tempo</li>
              <li>Mengubah spesifikasi teknis layanan untuk peningkatan kualitas dengan pemberitahuan</li>
            </ul>
            <p className="mt-4"><strong>Kewajiban Penyedia Layanan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Menyediakan layanan sesuai paket yang disepakati</li>
              <li>Menjaga keamanan dan kerahasiaan data pelanggan</li>
              <li>Memberikan dukungan teknis 24/7</li>
              <li>Menginformasikan jadwal maintenance dan gangguan</li>
              <li>Menyelesaikan gangguan teknis dalam waktu yang wajar sesuai SLA</li>
              <li>Memberikan kompensasi sesuai SLA jika gagal memenuhi jaminan layanan</li>
            </ul>
          </Section>

          <Section number="5" title="Biaya, Pembayaran & Denda Keterlambatan" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Biaya Instalasi:</strong> Dikenakan satu kali saat pemasangan pertama. Besaran tergantung paket dan lokasi. Beberapa paket menawarkan instalasi GRATIS sebagai promo.</li>
              <li><strong>Biaya Bulanan:</strong> Dibayarkan setiap bulan sesuai paket yang dipilih. Tagihan diterbitkan setiap tanggal <strong>1</strong> dan jatuh tempo pada tanggal <strong>20</strong> setiap bulannya.</li>
              <li><strong>Metode Pembayaran:</strong> Transfer bank (BCA, Mandiri, BNI, BRI, Bank Aceh), virtual account, e-wallet, atau pembayaran langsung di kantor Telnet Indonesia.</li>
              <li><strong>Denda Keterlambatan:</strong> Keterlambatan pembayaran dikenakan denda sebesar <strong>2% per bulan</strong> dari total tagihan yang belum dibayar.</li>
              <li><strong>Penangguhan Layanan:</strong> Layanan akan diturunkan kecepatannya (throttle) setelah <strong>7 hari</strong> keterlambatan, dan dinonaktifkan setelah <strong>30 hari</strong> keterlambatan.</li>
              <li><strong>Biaya Aktivasi Ulang:</strong> Pelanggan yang layanannya dinonaktifkan karena keterlambatan pembayaran akan dikenakan biaya aktivasi ulang.</li>
              <li><strong>Pajak:</strong> Seluruh harga belum termasuk PPN 11% (atau sesuai ketentuan perpajakan yang berlaku).</li>
            </ul>
          </Section>

          <Section number="6" title="Masa Berlaku & Perpanjangan Kontrak" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Kontrak Minimum:</strong> Masa berlangganan minimum adalah <strong>12 bulan</strong> untuk paket reguler dan <strong>24 bulan</strong> untuk paket dedicated.</li>
              <li><strong>Perpanjangan Otomatis:</strong> Kontrak akan diperpanjang secara otomatis setiap bulan setelah masa kontrak minimum berakhir, kecuali pelanggan mengajukan pembatalan.</li>
              <li><strong>Perubahan Paket:</strong> Upgrade paket dapat dilakukan kapan saja. Downgrade hanya dapat dilakukan setelah masa kontrak minimum berakhir.</li>
              <li><strong>Pemberitahuan:</strong> Pelanggan akan menerima notifikasi <strong>30 hari</strong> sebelum masa kontrak berakhir.</li>
            </ul>
          </Section>

          <Section number="7" title="Pembatalan & Pemutusan Layanan" isDarkMode={isDarkMode}>
            <p><strong>Pembatalan oleh Pelanggan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Pengajuan pembatalan harus disampaikan minimal <strong>30 hari</strong> sebelum tanggal efektif pembatalan</li>
              <li>Pembatalan sebelum masa kontrak minimum berakhir akan dikenakan <strong>biaya penalti</strong> sebesar sisa bulan kontrak × 50% biaya bulanan</li>
              <li>Pelanggan wajib mengembalikan seluruh perangkat milik Telnet Indonesia dalam kondisi baik</li>
              <li>Kerusakan atau kehilangan perangkat akan dikenakan biaya penggantian</li>
            </ul>
            <p className="mt-4"><strong>Pemutusan oleh Penyedia Layanan:</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Keterlambatan pembayaran lebih dari 30 hari</li>
              <li>Pelanggaran ketentuan penggunaan yang bersifat material</li>
              <li>Penggunaan layanan untuk aktivitas ilegal</li>
              <li>Penyalahgunaan jaringan yang mengganggu pelanggan lain</li>
            </ul>
          </Section>

          <Section number="8" title="Service Level Agreement (SLA)" isDarkMode={isDarkMode}>
            <div className={`rounded-xl p-6 border ${isDarkMode ? 'bg-green-900/20 border-green-800/40' : 'bg-green-50 border-green-100'}`}>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Jaminan Uptime</p>
                  <p className="text-3xl font-bold text-green-600">99.5%</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Per bulan kalender</p>
                </div>
                <div>
                  <p className={`font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Response Time</p>
                  <p className="text-3xl font-bold text-green-600">&lt; 4 jam</p>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Untuk gangguan kritis</p>
                </div>
              </div>
            </div>
            <ul className="list-disc pl-5 space-y-2 mt-4">
              <li><strong>Uptime 99.5%:</strong> Kami menjamin ketersediaan layanan minimal 99.5% dalam satu bulan kalender, tidak termasuk maintenance terjadwal.</li>
              <li><strong>Response Time:</strong> Gangguan kritis ditangani dalam &lt;4 jam, gangguan sedang dalam &lt;8 jam, dan gangguan ringan dalam &lt;24 jam.</li>
              <li><strong>Kompensasi:</strong> Jika uptime bulanan di bawah 99.5%, pelanggan berhak atas kompensasi berupa kredit tagihan proporsional:
                <ul className="list-disc pl-5 mt-1">
                  <li>99.0% - 99.5%: Kredit 5% dari tagihan bulanan</li>
                  <li>95.0% - 99.0%: Kredit 10% dari tagihan bulanan</li>
                  <li>Di bawah 95.0%: Kredit 25% dari tagihan bulanan</li>
                </ul>
              </li>
              <li><strong>Pengecualian SLA:</strong> Force majeure, maintenance terjadwal, gangguan yang disebabkan pelanggan, atau gangguan pada perangkat milik pelanggan.</li>
            </ul>
          </Section>

          <Section number="9" title="Force Majeure" isDarkMode={isDarkMode}>
            <p>Telnet Indonesia tidak bertanggung jawab atas kegagalan atau keterlambatan dalam penyediaan layanan yang disebabkan oleh keadaan di luar kendali wajar, termasuk namun tidak terbatas pada:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Bencana alam (gempa bumi, banjir, tsunami, tanah longsor)</li>
              <li>Pandemi atau wabah penyakit</li>
              <li>Perang, kerusuhan, atau aksi terorisme</li>
              <li>Kebijakan pemerintah yang membatasi operasional</li>
              <li>Gangguan pada infrastruktur nasional (listrik PLN, jaringan backbone nasional)</li>
              <li>Kerusakan kabel bawah laut (submarine cable)</li>
              <li>Serangan siber berskala besar (DDoS attack terhadap infrastruktur nasional)</li>
            </ul>
          </Section>

          <Section number="10" title="Penyelesaian Sengketa" isDarkMode={isDarkMode}>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Musyawarah:</strong> Segala perselisihan diselesaikan terlebih dahulu secara musyawarah mufakat antara para pihak dalam waktu <strong>30 hari kerja</strong>.</li>
              <li><strong>Mediasi:</strong> Jika musyawarah tidak mencapai kesepakatan, para pihak sepakat untuk menempuh jalur mediasi melalui mediator independen yang disepakati bersama.</li>
              <li><strong>Arbitrase/Pengadilan:</strong> Jika mediasi tidak berhasil, sengketa akan diselesaikan melalui Badan Arbitrase Nasional Indonesia (BANI) atau Pengadilan Negeri Banda Aceh.</li>
              <li><strong>Pengaduan Resmi:</strong> Pelanggan juga dapat mengajukan pengaduan ke Badan Regulasi Telekomunikasi Indonesia (BRTI) atau Yayasan Lembaga Konsumen Indonesia (YLKI).</li>
            </ul>
          </Section>

          <Section number="11" title="Hukum yang Berlaku" isDarkMode={isDarkMode}>
            <p>Ketentuan Layanan ini tunduk pada dan ditafsirkan sesuai dengan hukum yang berlaku di Republik Indonesia, termasuk:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Undang-Undang No. 36 Tahun 1999 tentang Telekomunikasi</li>
              <li>Undang-Undang No. 11 Tahun 2008 tentang Informasi dan Transaksi Elektronik (ITE) beserta perubahannya</li>
              <li>Undang-Undang No. 27 Tahun 2022 tentang Pelindungan Data Pribadi</li>
              <li>Undang-Undang No. 8 Tahun 1999 tentang Perlindungan Konsumen</li>
              <li>Peraturan Pemerintah No. 71 Tahun 2019 tentang Penyelenggaraan Sistem dan Transaksi Elektronik</li>
              <li>Peraturan Menteri Kominfo terkait penyelenggaraan jasa telekomunikasi</li>
            </ul>
          </Section>

          {/* Footer note */}
          <div className={`mt-10 pt-6 border-t text-center ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Dengan menggunakan layanan Telnet Indonesia, Anda dianggap telah membaca, memahami, dan menyetujui seluruh Ketentuan Layanan ini.
            </p>
            <div className="flex justify-center gap-4 mt-4 text-sm">
              <Link to="/kebijakan-privasi" className="text-green-600 hover:underline">Kebijakan Privasi</Link>
              <Link to="/alur-transaksi" className="text-green-600 hover:underline">Alur Transaksi</Link>
              <Link to="/faq" className="text-green-600 hover:underline">FAQ</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default KetentuanLayanan;
